// src/components/ErrorBoundary.js — AVS Cloud Error Boundary
import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] React render crash:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const styles = {
      wrapper: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0a0e1a',
        padding: '1.5rem',
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
      },
      card: {
        width: '100%',
        maxWidth: '520px',
        background: '#111827',
        border: '1px solid #1e2d3d',
        borderRadius: '16px',
        padding: '2.5rem',
        boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
        textAlign: 'center',
      },
      icon: {
        fontSize: '3rem',
        marginBottom: '1rem',
      },
      title: {
        color: '#f1f5f9',
        fontSize: '1.5rem',
        fontWeight: 700,
        marginBottom: '0.5rem',
      },
      subtitle: {
        color: '#64748b',
        fontSize: '0.875rem',
        marginBottom: '1.5rem',
        lineHeight: 1.6,
      },
      errorBox: {
        background: '#0d1117',
        border: '1px solid #2d3748',
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '1.5rem',
        textAlign: 'left',
        overflowX: 'auto',
      },
      errorText: {
        color: '#f87171',
        fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
        fontSize: '0.78rem',
        lineHeight: 1.6,
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
        margin: 0,
      },
      btnRow: {
        display: 'flex',
        gap: '0.75rem',
        justifyContent: 'center',
        flexWrap: 'wrap',
      },
      btnPrimary: {
        background: 'linear-gradient(135deg, #06b6d4, #0ea5e9)',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        padding: '0.7rem 1.4rem',
        fontSize: '0.875rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'opacity 0.2s',
      },
      btnSecondary: {
        background: 'transparent',
        color: '#94a3b8',
        border: '1px solid #2d3748',
        borderRadius: '8px',
        padding: '0.7rem 1.4rem',
        fontSize: '0.875rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'border-color 0.2s, color 0.2s',
      },
    };

    const errorMessage = this.state.error
      ? (this.state.error.message || String(this.state.error))
      : 'Unbekannter Fehler';

    return (
      <div style={styles.wrapper}>
        <div style={styles.card}>
          <div style={styles.icon}>⚠️</div>
          <h1 style={styles.title}>Etwas ist schiefgelaufen</h1>
          <p style={styles.subtitle}>
            Ein unerwarteter Fehler ist aufgetreten. Du kannst die Seite neu laden
            oder zum Dashboard zurückkehren.
          </p>
          <div style={styles.errorBox}>
            <pre style={styles.errorText}>{errorMessage}</pre>
          </div>
          <div style={styles.btnRow}>
            <button
              style={styles.btnPrimary}
              onClick={() => window.location.reload()}
            >
              Seite neu laden
            </button>
            <button
              style={styles.btnSecondary}
              onClick={() => { window.location.href = '/'; }}
            >
              Zurück zum Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
